import { cloneDeep, map, max, orderBy } from 'lodash';
import {
  Asset as RemotionAsset,
  AUDIO_DELAY_FRAMES,
  AudioTrack,
  CaptionSettings,
  RemotionConfig,
  Subtitle,
} from '../remotion-types';
import {
  AspectRatio,
  Asset,
  AssetType,
  defaultThumbnailConfig,
  getSceneItems,
  GLOBAL_SCENE_INDEX,
  isRTL,
  Languages,
  Scene,
  SceneAssetItem,
  SceneAudioItem,
  SceneItem,
  SceneItemType,
  SceneVoiceoverItem,
  SubtitlesConfig,
  Video,
} from '@monorepo/types';
import { TrackGroup, TrackItem, TrackType } from '../remotion-tracks';

export const aspectRatioDimensions = {
  [AspectRatio.NineBySixteen]: {
    compositionWidth: 1080,
    compositionHeight: 1920,
  },
  [AspectRatio.SixteenByNine]: {
    compositionWidth: 1920,
    compositionHeight: 1080,
  },
  [AspectRatio.OneByOne]: {
    compositionWidth: 1080,
    compositionHeight: 1080,
  },
};

// Mapping between TrackType and AssetType
export const trackTypeToAssetType: Record<string, TrackType> = {
  [AssetType.Image]: TrackType.Image,
  [AssetType.Video]: TrackType.Video,
};

// Deter

export function createSubtitlesWithWordGroups(
  orderedScenes: any[],
  wordsPerGroup = 4
) {
  let currentTime = 0;
  const subtitles: Subtitle[] = [];
  const subtitlesByScenes: Record<string, Subtitle[]> = {};

  orderedScenes.forEach((scene, sceneIndex) => {
    const additionInSeconds = AUDIO_DELAY_FRAMES / 30;

    // TODO: take considration multiple voiceovers
    const sceneVoiceoverItems = scene.items.filter(
      (sceneItem) => sceneItem.type === SceneItemType.Voiceover
    );

    sceneVoiceoverItems.forEach((sceneVoiceoverItem) => {
      const sceneSubtitles: Subtitle[] = [];

      const sceneVoiceoverData = sceneVoiceoverItem?.data as SceneVoiceoverItem;
      const sceneWords = sceneVoiceoverData?.words || [];

      for (let i = 0; i < sceneWords.length; i += wordsPerGroup) {
        const wordGroup = sceneWords.slice(i, i + wordsPerGroup);

        if (wordGroup.length > 0) {
          const startTime = currentTime + wordGroup[0].start;
          const endTime = currentTime + wordGroup[wordGroup.length - 1].end;

          let subtitle: Subtitle = {
            startTime,
            endTime,
            words: [],
          };

          for (let j = 0; j < wordGroup.length; j++) {
            const word = wordGroup[j];
            const wordStartTime = currentTime + word.start;
            const wordEndTime = currentTime + word.end;

            const nextWordStartTime = currentTime + wordGroup[j + 1]?.start;
            const gapDiff = nextWordStartTime - wordEndTime;
            const isGap = wordEndTime !== nextWordStartTime && gapDiff > 0.09;

            // ZERO TIME WORD GAP -> SHOULD BE SYNC WITH ALSO SOUNDS OR IT"S A PROBLEM
            // const isZero = wordEndTime - wordStartTime === 0;
            //
            // if (isZero) {
            //   wordEndTime += 0.3;
            //   currentTime+= 0.3;
            //   console.log(wordStartTime, wordEndTime, word);
            // }

            subtitle.words.push({
              sceneId: scene._id,
              sceneItemIndex: sceneVoiceoverItem.index,
              startTime: wordStartTime,
              endTime: wordEndTime,
              text: word.word,
            });

            if (isGap && wordGroup[j + 1]) {
              const nextWordEndTimeTime = currentTime + wordGroup[j + 1].end;

              subtitle.endTime = wordEndTime;

              subtitles.push(subtitle);
              sceneSubtitles.push(subtitle);
              subtitle = {
                startTime: nextWordStartTime,
                endTime: nextWordEndTimeTime,
                words: [],
              };
            }
          }

          subtitles.push(subtitle);
          sceneSubtitles.push(subtitle);
        }
      }

      subtitlesByScenes[`${sceneIndex}-${sceneVoiceoverItem.index}`] =
        sceneSubtitles;
    });

    currentTime += scene.duration;
  });

  return { subtitles, subtitlesByScenes };
}

export function buildConfig({
  backgroundMusicUrl,
  backgroundMusicVolume,
  subtitlesConfig,
  video,
  assets,
  globalScene,
  scenes,
  language = Languages.English,
}: {
  backgroundMusicUrl?: string;
  backgroundMusicVolume?: number;
  video: Video;
  globalScene?: Scene | null;
  scenes: Scene[];
  subtitlesConfig?: SubtitlesConfig;
  assets: Asset[];
  language?: Languages;
}) {
  // Create asset URL lookup map
  const assetMap = new Map<string, Asset>(
    assets.map((asset) => [asset._id.toString(), asset])
  );

  // Sort scenes by index
  const sortedScenes = orderBy(cloneDeep(scenes), 'index', 'asc');
  const FPS = 30;

  const durationInFrames = video.duration * FPS;

  const audioTracks: AudioTrack[][] = [];

  const thumbnailConfig = video.thumbnailConfig || defaultThumbnailConfig;
  const thumbnailDurationInSeconds =
    thumbnailConfig.enableVoiceover && thumbnailConfig.voiceoverUrl
      ? thumbnailConfig.duration
      : 0.05;

  if (thumbnailConfig.enabled) {
    const firstScene = sortedScenes[0];

    if (firstScene) {
      const firstSceneVoiceover = firstScene.items.find(
        (sceneItem) => sceneItem.type === SceneItemType.Voiceover
      );
      const firstSceneAsset = firstScene.items.find(
        (sceneItem) => sceneItem.type === SceneItemType.Asset
      );

      if (firstSceneVoiceover && firstSceneAsset) {
        firstScene.duration += thumbnailDurationInSeconds;
        // firstSceneAsset.end += thumbnailDurationInSeconds;

        const thumbnailSceneItems: SceneItem[] = [];

        if (thumbnailConfig.enableVoiceover && thumbnailConfig.voiceoverUrl) {
          thumbnailSceneItems.push({
            index: 0,
            start: 0,
            end: thumbnailDurationInSeconds,
            type: SceneItemType.Voiceover,
            data: {
              url: thumbnailConfig.voiceoverUrl,
              duration: thumbnailDurationInSeconds,
              words: [],
            },
          });
        }

        firstScene.items.forEach((sceneItem, sceneItemIndex) => {
          sceneItem.index = sceneItemIndex + 2;

          if (thumbnailConfig.enableVoiceover) {
            sceneItem.start += thumbnailDurationInSeconds;
            sceneItem.end += thumbnailDurationInSeconds;
          }
        });

        firstSceneAsset.start = 0;

        if (!thumbnailConfig.enableVoiceover) {
          firstSceneVoiceover.start = 0;
        }

        (firstSceneVoiceover.data as SceneVoiceoverItem).words.forEach(
          (word) => {
            word.start += thumbnailDurationInSeconds;
            word.end += thumbnailDurationInSeconds;
          }
        );

        firstScene.items = [...thumbnailSceneItems, ...firstScene.items];
      }
    }
  }

  let captionSettings: Partial<CaptionSettings> = {};

  if (subtitlesConfig) {
    captionSettings = {
      display: subtitlesConfig.display,
      numberOfWords: subtitlesConfig.numberOfWords,
      uppercase: subtitlesConfig.uppercase,
      fontSize: subtitlesConfig.fontSize,
      fontFamily: subtitlesConfig.fontName,
      textColor: subtitlesConfig.primaryColor,
      currentWordColor: subtitlesConfig.highlightColor,
      shadowColor: subtitlesConfig.outlineColor,
      shadowBlur: subtitlesConfig.outline,
      currentWordBackgroundColor: subtitlesConfig.backColor || 'transparent',
      positionY: subtitlesConfig.positionY,
    };
  }

  const remotionAssets: RemotionAsset[] = [];

  const { subtitles, subtitlesByScenes } = createSubtitlesWithWordGroups(
    sortedScenes,
    captionSettings?.numberOfWords || 2
  );

  const hasCollision = (track1: TrackItem, track2: TrackItem): boolean => {
    // Round the values to handle floating-point precision
    const track1Start = Math.round(track1.from);
    const track1End = Math.round(track1.from + track1.durationInFrames);
    const track2Start = Math.round(track2.from);
    const track2End = Math.round(track2.from + track2.durationInFrames);

    // console.log(track1End, track2End,
    //   `track1.from ${track1Start} < ${track2End} and ${track1End} > ${track2Start}`,
    //   track1Start < track2End && track1End > track2Start
    // );

    // Check if tracks overlap by more than 1 frame
    return track1Start < track2End && track1End > track2Start;
  };

  const splitAssetGroupIfNeeded = (
    groupNeedsToBeSplit: TrackGroup,
    groupType: TrackType | 'asset' = 'asset'
  ): TrackGroup[] => {
    if (groupNeedsToBeSplit.tracks.length <= 1) return [groupNeedsToBeSplit];

    // Sort tracks by start time
    const sortedTracks = [...groupNeedsToBeSplit.tracks].sort(
      (a, b) => a.from - b.from
    );
    const groups: TrackItem[][] = [];
    let currentGroup: TrackItem[] = [sortedTracks[0]];

    for (let i = 1; i < sortedTracks.length; i++) {
      const currentTrack = sortedTracks[i];
      const prevTrack = sortedTracks[i - 1];
      const hasCollisionWithGroup = currentGroup.some((groupTrack) =>
        hasCollision(currentTrack, groupTrack)
      );
      const hasFrameGap =
        Math.round(
          currentTrack.from - (prevTrack.from + prevTrack.durationInFrames)
        ) > 1;

      // console.log(hasFrameGap, hasCollisionWithGroup, currentTrack, currentTrack.from - (prevTrack.from + prevTrack.durationInFrames))

      if (hasCollisionWithGroup || hasFrameGap) {
        // Start a new group if there's a collision or frame gap
        groups.push(currentGroup);
        currentGroup = [currentTrack];
      } else {
        // Add to current group if no collision and no frame gap
        currentGroup.push(currentTrack);
      }
    }

    // Add the last group
    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    // Convert groups to TrackGroup format
    return groups.map((tracks, index) => ({
      tracks,
      type: groupType,
      index: groupNeedsToBeSplit.index + index, // Maintain relative ordering from original group
    }));
  };

  // Initialize two main groups
  const trackGroups: TrackGroup[] = [];

  const assetGroup: TrackGroup = {
    tracks: [],
    type: 'asset',
    index: -1,
  };

  const voiceoverGroup: TrackGroup = {
    tracks: [],
    type: TrackType.Voiceover,
    index: 0,
  };

  const allTrackGroup: TrackGroup = {
    tracks: [],
    type: TrackType.Track,
    index: 1,
  };

  const ratios =
    aspectRatioDimensions[video.aspectRatio || AspectRatio.NineBySixteen];

  const createTrackItemFromSceneItem = (
    item: SceneItem,
    {
      previousScenesDuration,
      sceneIndex,
    }: { previousScenesDuration: number; sceneIndex: number }
  ) => {
    // Common properties for all track items
    const commonProps = {
      from: previousScenesDuration + item.start * FPS,
      durationInFrames: (item.end - item.start) * FPS,
      left: item.left || 0,
      top: item.top || 0,
      width: item.width || ratios.compositionWidth,
      height: item.height || ratios.compositionHeight,
    };

    if (item.type === SceneItemType.Asset) {
      const sceneAsset = item.data as SceneAssetItem;
      const asset = assetMap.get(sceneAsset.assetId);
      const type = asset?.type || AssetType.Image;
      const src = asset?.url || '';
      const format = asset?.format || '';

      const track: TrackItem = {
        ...commonProps,
        type: trackTypeToAssetType[type],
        data: {
          type,
          src,
          contentType: format,
          motion: sceneAsset.motion,
        },
        transition: sceneAsset.transition
          ? {
              outEffect: { effect: sceneAsset.transition },
            }
          : undefined,
      };

      return track;
    }

    if (item.type === SceneItemType.Voiceover) {
      const voiceoverItem = item.data as SceneVoiceoverItem;
      const track: TrackItem = {
        ...commonProps,
        type: TrackType.Voiceover,
        data: {
          src: `${voiceoverItem.url}?d=${voiceoverItem.duration}`,
          startAt: previousScenesDuration,
          duration: voiceoverItem.duration,
          subtitles: subtitlesByScenes[`${sceneIndex}-${item.index}`],
          subtitlesConfig: captionSettings,
        },
      };

      return track;
    }

    if (item.type === SceneItemType.Audio) {
      const audioItem = item.data as SceneAudioItem;
      const track: TrackItem = {
        ...commonProps,
        type: TrackType.Audio,
        data: {
          src: `${audioItem.url}?d=${audioItem.name}&v=${audioItem.volume}&pr=${audioItem.playbackRate}&l=${audioItem.loop}`,
          loop: true,
          volume: audioItem.volume,
          playbackRate: audioItem.playbackRate,
          startAt: previousScenesDuration,
          duration: audioItem.duration,
        },
      };

      return track;
    }

    return null;
  };

  // Fill the groups with tracks
  sortedScenes.forEach((scene, sceneIndex) => {
    // Calculate start frame for current scene based on previous scenes
    const previousScenesDuration = sortedScenes
      .slice(0, sceneIndex)
      .reduce((sum, prevScene) => sum + prevScene.duration * FPS, 0);

    const items = getSceneItems({ scene, video });

    items.forEach((item: SceneItem) => {
      const trackItem = createTrackItemFromSceneItem(item, {
        previousScenesDuration,
        sceneIndex,
      });

      if (trackItem) {
        if (item.type === SceneItemType.Asset) {
          assetGroup.tracks.push(trackItem);
        }
        if (item.type === SceneItemType.Voiceover) {
          voiceoverGroup.tracks.push(trackItem);
        }
        if (item.type === SceneItemType.Audio) {
          allTrackGroup.tracks.push(trackItem);
        }
      }
    });
  });

  trackGroups.push(voiceoverGroup);

  assetGroup.tracks = orderBy(assetGroup.tracks, ['from'], ['asc']);

  if (assetGroup.tracks.length > 0) {
    const newGroups = splitAssetGroupIfNeeded(assetGroup);

    const lastIndex = max(map(trackGroups, 'index')) || 0;

    newGroups.forEach((group, index) => {
      trackGroups.push({
        tracks: group.tracks,
        type: 'asset',
        index: lastIndex + 1 + index,
      });
    });
  }

  if (allTrackGroup.tracks.length > 0) {
    const newGroups = splitAssetGroupIfNeeded(allTrackGroup);

    const lastIndex = max(map(trackGroups, 'index')) || 0;

    newGroups.forEach((group, index) => {
      trackGroups.push({
        tracks: group.tracks,
        type: TrackType.Track,
        index: lastIndex + 1 + index,
      });
    });
  }

  if (
    video.backgroundMusicId &&
    backgroundMusicUrl &&
    !globalScene?.musicMigrated
  ) {
    trackGroups.push({
      tracks: [
        {
          from: 0,
          durationInFrames,
          type: TrackType.Audio,
          data: {
            src: backgroundMusicUrl,
            startAt: 0,
            volume: backgroundMusicVolume || 0.1,
            loop: true,
            duration: durationInFrames,
          },
        },
      ],
      type: TrackType.Audio,
      index: trackGroups.length,
    });
  }

  if (thumbnailConfig.enabled) {
    const thumbnailTracks: TrackItem[] = [
      {
        from: 0,
        durationInFrames: thumbnailDurationInSeconds * 30,
        left: 0,
        top: '40%',
        width: '100%',
        height: 'auto',
        type: TrackType.Thumbnail,
        data: {
          type: video.thumbnailConfig.type,
          text: video.thumbnailConfig.text,
        },
      },
    ];

    trackGroups.push({
      tracks: thumbnailTracks,
      type: TrackType.Thumbnail,
      index: trackGroups.length,
    });
  }

  // Process globalScene if provided
  if (globalScene) {
    // Create a group for global scene items
    const globalAssetGroup: TrackGroup = {
      tracks: [],
      type: TrackType.Track,
      index: trackGroups.length, // Position after all existing track groups
    };

    const items = globalScene.items;

    // Process each item in the global scene
    items.forEach((item: SceneItem) => {
      // For global scene items, we want them to start at the beginning and span the entire video
      const trackItem = createTrackItemFromSceneItem(item, {
        previousScenesDuration: 0,
        sceneIndex: GLOBAL_SCENE_INDEX,
      });

      if (trackItem) {
        // Override the duration to span the entire video
        globalAssetGroup.tracks.push(trackItem);
      }
    });

    // Split the global asset group if needed and add them to trackGroups
    if (globalAssetGroup.tracks.length > 0) {
      globalAssetGroup.tracks = orderBy(
        globalAssetGroup.tracks,
        ['from'],
        ['asc']
      );
      const splitGlobalGroups = splitAssetGroupIfNeeded(globalAssetGroup);

      const lastIndex = max(map(trackGroups, 'index')) || 0;

      splitGlobalGroups.forEach((group, index) => {
        trackGroups.push({
          tracks: group.tracks,
          type: group.type,
          index: lastIndex + 1 + index,
        });
      });
    }
  }

  const config: RemotionConfig = {
    trackGroups,
    assets: remotionAssets,
    subtitles,
    isRtl: isRTL(language),
    captionSettings: captionSettings,
    width: ratios.compositionWidth,
    height: ratios.compositionHeight,
    fps: 30,
    durationInFrames: 90,
  };

  config.durationInFrames = durationInFrames;

  if (thumbnailConfig.enabled) {
    config.durationInFrames += Math.round(thumbnailDurationInSeconds * 30);
  }

  config.trackGroups = config.trackGroups.reverse();

  return config;
}
